import type { ReactNode } from "react";
import { cn } from "../cn";

/** Skeleton sized like the real card (plan: no full-screen spinners). */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        "animate-shimmer rounded-xl bg-[linear-gradient(90deg,var(--color-surface-container)_0%,var(--color-surface-container-low)_50%,var(--color-surface-container)_100%)] bg-[length:200%_100%]",
        className,
      )}
    />
  );
}

export interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
}

/** One sentence and one action. */
export function EmptyState({ icon, title, description, action, className, compact }: EmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center text-center", compact ? "gap-2 py-6" : "gap-3 py-12", className)}>
      {icon && (
        <div className="flex size-12 items-center justify-center rounded-2xl bg-primary-fixed/60 text-primary [&_svg]:size-6">{icon}</div>
      )}
      <div className="flex max-w-sm flex-col gap-1">
        <p className="text-headline-sm text-on-surface">{title}</p>
        {description && <p className="text-body-md text-on-surface-variant">{description}</p>}
      </div>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

export interface ProgressRingProps {
  value: number; // 0–100
  size?: number;
  stroke?: number;
  label?: ReactNode;
  className?: string;
}

export function ProgressRing({ value, size = 48, stroke = 4, label, className }: ProgressRingProps) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className={cn("relative inline-flex items-center justify-center", className)} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-surface-container-high)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--color-primary-container)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c - (clamped / 100) * c}
          className="transition-[stroke-dashoffset] duration-700 ease-out-soft"
        />
      </svg>
      {label && <span className="absolute text-[10px] font-bold text-on-surface tabular">{label}</span>}
    </div>
  );
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  count?: number;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  label: string;
  size?: "sm" | "md";
  className?: string;
}

export function SegmentedControl<T extends string>({ value, onChange, options, label, size = "md", className }: SegmentedControlProps<T>) {
  return (
    <div role="radiogroup" aria-label={label} className={cn("inline-flex rounded-xl bg-surface-container p-1", className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold whitespace-nowrap transition-all duration-150 [&_svg]:size-4",
              size === "sm" ? "h-8 px-2.5 text-label-md" : "h-9 px-3.5 text-label-lg",
              active ? "bg-surface-container-lowest text-primary shadow-card" : "text-on-surface-variant hover:text-on-surface",
            )}
          >
            {o.icon}
            {o.label}
            {o.count !== undefined && (
              <span className={cn("rounded-full px-1.5 text-[10px] font-bold tabular", active ? "bg-primary-fixed" : "bg-surface-container-high")}>
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
